import { useCapcoEnabled } from '@/app/useCapcoEnabled';
import { MARKING_OPTIONS, isListedMarking, markingLevel, portionMarkTitle, type MarkingOption } from './marking';
import './marking.css';

export interface PortionMarkProps {
  /** The marking as stored, e.g. `C`, `S//NF`. Empty or absent: nothing is rendered. */
  marking: string | null | undefined;
  /** The markings whose full title is known. Defaults to U, C, S, TS. */
  options?: readonly MarkingOption[];
  className?: string;
  /**
   * Follow the mark with a space, only when a mark is rendered. For a mark
   * placed as a flex SIBLING of the text it marks: the row's gap lays it out
   * and drops the space, which is there so the accessible name and copied
   * text read `(C) Name`, not `(C)Name`.
   */
  spaceAfter?: boolean;
}

/**
 * The read-only portion mark of ONE text: `(C)`, `(S//NF)`, written before
 * the text it marks, as in a document.
 *
 * - It renders NOTHING, no element at all, when CAPCO is disabled for the
 *   deployment (`useCapcoEnabled`) or when `marking` is empty or blank. A line
 *   that holds it is then exactly the line it was without it.
 * - The marking is shown exactly as stored. One outside the list (`S//NF`) is
 *   shown as itself, never as `U`: that would display a lower marking than
 *   the one stored.
 * - **A mark keeps its natural width while the line has room, and is never
 *   cut silently.** In a row the text beside it gives way first. A mark that
 *   must shrink ends in an ellipsis INSIDE the parentheses: the part after
 *   the classification (from the first `//`) first, and the classification
 *   itself only when even `(S…)` does not fit. The closing parenthesis always
 *   shows. The full marking is the tooltip and the accessible name whatever
 *   is visible. The CSS is in marking.css; it uses no percentage width.
 * - The level's colour is the bar on its left edge, the same cue and the same
 *   tokens as the edit pill. An unknown level gets the neutral bar. The text
 *   always says the marking, so colour is never the only signal.
 * - `role="img"` with an `aria-label`: assistive technology reads the full
 *   form ("Classification: CONFIDENTIAL") in place of the abbreviation, and
 *   the same words are the tooltip.
 *
 * It has no margin of its own. `MarkedText` puts a space after it; a call
 * site that places it as a flex sibling of a truncating element relies on the
 * row's gap.
 *
 * To decide what an OBJECT's label shows, use `MarkedText` or one of
 * `labelMarking` / `storedLabelMarking` / `documentNameMarking`; this
 * component only draws a string.
 */
export default function PortionMark({ marking, options = MARKING_OPTIONS, className, spaceAfter }: PortionMarkProps) {
  const enabled = useCapcoEnabled();
  if (!enabled || !marking?.trim()) return null;

  const title = portionMarkTitle(marking, options);
  // Split only for layout: the classification, then everything from the first
  // `//`. Joined, the text is the marking exactly as stored.
  const cut = marking.indexOf('//');
  const head = cut < 0 ? marking : marking.slice(0, cut);
  const tail = cut < 0 ? '' : marking.slice(cut);
  const mark = (
    <span
      role="img"
      className={className ? `portion-mark ${className}` : 'portion-mark'}
      data-level={markingLevel(marking) ?? undefined}
      // A listed mark is short and is never cut (marking.css).
      data-listed={isListedMarking(marking, options) ? 'true' : undefined}
      title={title}
      aria-label={title}
    >
      <span className="portion-mark-head">({head}</span>
      {tail && <span className="portion-mark-tail">{tail}</span>}
      <span className="portion-mark-close">)</span>
    </span>
  );
  return spaceAfter ? <>{mark} </> : mark;
}
